// Pack desk: "Items coming from Flagged Picks" (per Alex, 2026-10-08).
//
// Shown INSTEAD of the pack screen the moment an order with a live flagged
// pick is opened, so the packer reads it before the first scan.
//
// Every unit on the shelf
//   The packer fetches each unit and SCANS it here. The scan is the pack
//   scan — the parent marks the line scanned and the unit goes in box 1,
//   so the pack screen opens with it already ticked (2026-10-09: before
//   this the notice was read-only and the unit had to be scanned twice).
//   Once every shelved unit is in, the pack screen opens on its own.
//
// A unit still on its way
//   The packer is told, and the only way on is to FLAG the order with the
//   preset "Awaiting flagged pick" reason — nothing is scanned, the tote is
//   set aside with the flag label, and the leader returns the order to the
//   pack queue once the unit is shelved. Never ship short from here.
import { useEffect, useRef, useState } from "react";
import { Flag, PackageSearch, ArrowRight, ArrowLeft, Check, ScanBarcode } from "lucide-react";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { scannableBarcodes } from "./itemBarcodes";
import type { DispatchOrder, DispatchOrderItem, FlaggedPickInfo } from "../../types/Dispatch";

// Server-side constant stamped on the flag so the Flagged tile recognises
// the family whatever locale the desk is in (see flagged-picks.js).
export const AWAITING_FLAGGED_PICK_REASON = "Awaiting flagged pick";

export const flaggedPickItems = (order: DispatchOrder): DispatchOrderItem[] =>
    order.items.filter(i => !!i.flagged_pick);

// The shelf as people read it ("FP1L1"), never the barcode the scanner
// sends. Older payloads carry only the barcodes; show those sooner than
// nothing.
export const stagingShelfLabel = (fp: Pick<FlaggedPickInfo, "staging_locations" | "staging_labels">): string =>
    (fp.staging_labels && fp.staging_labels.length > 0 ? fp.staging_labels : fp.staging_locations).join(", ");

export const hasPendingFlaggedPick = (order: DispatchOrder): boolean =>
    order.items.some(i => i.flagged_pick && i.flagged_pick.status !== "staged");

// How many of a line's shelved units the packer has scanned so far. The
// scans land on the line's scannedCount (the parent applies them), so the
// identifying scan that opened the order counts too — no local tally that
// could drift from the pack screen.
export const collectedCount = (item: DispatchOrderItem): number =>
    Math.min(item.scannedCount || 0, item.flagged_pick?.qty ?? 0);

export const allShelvedCollected = (order: DispatchOrder): boolean =>
    flaggedPickItems(order)
        .filter(i => i.flagged_pick!.status === "staged")
        .every(i => collectedCount(i) >= i.flagged_pick!.qty);

type Props = {
    order: DispatchOrder;
    onContinue: () => void;
    onFlag: () => void;
    /** A shelved unit was scanned: mark one unit of this line as packed
     *  (scannedCount + box 1), exactly as the pack screen would. */
    onCollect?: (item: DispatchOrderItem) => void;
    /** Leave the order without flagging or packing it. */
    onLeave?: () => void;
    readOnly?: boolean;
};

const FlaggedPickNotice = ({ order, onContinue, onFlag, onCollect, onLeave, readOnly }: Props) => {
    const { t } = useTranslation();
    const items = flaggedPickItems(order);
    const pending = hasPendingFlaggedPick(order);
    const allCollected = allShelvedCollected(order);
    const [scanValue, setScanValue] = useState("");
    const [flashId, setFlashId] = useState<string | null>(null);
    const [shakeId, setShakeId] = useState<string | null>(null);
    const autoRef = useRef(false);

    // Every shelved unit scanned and nothing on its way: straight to the
    // pack screen, once. (Guarded so a re-render cannot fire it twice.)
    useEffect(() => {
        if (pending || !allCollected || items.length === 0 || autoRef.current) return;
        if (!items.some(i => collectedCount(i) > 0)) return; // nothing scanned here — leave Continue to the packer
        autoRef.current = true;
        onContinue();
    }, [allCollected, items, onContinue, pending]);

    const doSubmit = (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        setScanValue("");
        if (!val || readOnly || !onCollect) return;
        const match = items.find(i =>
            i.flagged_pick!.status === "staged"
            && collectedCount(i) < i.flagged_pick!.qty
            && (i.sku === val || scannableBarcodes(i).includes(val)));
        if (!match) {
            playError();
            const known = items.find(i => i.sku === val || scannableBarcodes(i).includes(val));
            setShakeId(known?.item_id ?? "none");
            window.setTimeout(() => setShakeId(null), 400);
            return;
        }
        onCollect(match);
        playSuccess();
        setFlashId(match.item_id);
        window.setTimeout(() => setFlashId(null), 400);
    };

    return (
        <div className="dispatch-page page-data fp-notice-page" data-testid="fp-notice">
            <div className="fp-notice">
                <div className="fp-notice-head">
                    <span className={`fp-notice-icon${pending ? " fp-notice-icon--pending" : ""}`}>
                        {pending ? <Flag size={20} /> : <PackageSearch size={20} />}
                    </span>
                    <h2>{pending ? t("fp_notice_title_pending") : t("fp_notice_title")}</h2>
                </div>
                <p className="fp-notice-body">{pending ? t("fp_notice_body_pending") : t("fp_notice_body_scan")}</p>
                {/* Found items (Poland, Alex 2026-10-20): never wait for a
                    found unit that is not on the shelf — ship without it.
                    The BE keeps an unscanned one off the order. */}
                {!pending && items.some(i => i.flagged_pick!.found_item && i.flagged_pick!.status === "staged" && collectedCount(i) < i.flagged_pick!.qty) && (
                    <p className="fp-notice-body fp-notice-found-hint" data-testid="fp-notice-found-hint">{t("fp_notice_found_hint")}</p>
                )}
                <div className="fp-notice-list">
                    {items.map(item => {
                        const fp = item.flagged_pick!;
                        const staged = fp.status === "staged";
                        const got = collectedCount(item);
                        const done = staged && got >= fp.qty;
                        const cls = ["fp-notice-item"];
                        if (staged) cls.push("fp-notice-item--staged");
                        if (done) cls.push("fp-notice-item--done");
                        if (flashId === item.item_id) cls.push("fp-notice-item--flash");
                        if (shakeId === item.item_id || shakeId === "none") cls.push("fp-notice-item--shake");
                        return (
                            <div key={item.item_id} className={cls.join(" ")} data-testid="fp-notice-item" data-collected={got}>
                                {item.image_url
                                    ? <img className="fp-task-img" src={item.image_url} alt="" />
                                    : <div className="fp-task-img fp-task-img--empty"><PackageSearch size={20} /></div>}
                                <div className="fp-task-main">
                                    <span className="fp-task-title">{item.title || item.sku}</span>
                                    <div className="fp-task-ids">
                                        <span>{item.sku}</span>
                                        {item.barcode && item.barcode !== item.sku && <span>{item.barcode}</span>}
                                    </div>
                                    {staged
                                        ? <span className="fp-notice-item-count" data-testid="fp-notice-count">{done ? <Check size={14} /> : <ScanBarcode size={14} />}{t("fp_notice_collected", { got: String(got), qty: String(fp.qty) })}</span>
                                        : <span className="fp-notice-item-pending">{fp.status === "awaiting_stock" ? t("fp_notice_awaiting_stock") : fp.status === "picking" && fp.picked_by_name ? t("fp_notice_picking", { name: fp.picked_by_name }) : t("fp_notice_pending")}</span>}
                                </div>
                                {staged
                                    ? <span className="fp-notice-item-shelf" data-testid="fp-notice-shelf">{stagingShelfLabel(fp)}</span>
                                    : <span className="fp-notice-item-qty">x{fp.qty}</span>}
                            </div>
                        );
                    })}
                </div>
                <div className="fp-notice-actions">
                    {pending ? (
                        <button type="button" className="btn btn-primary fp-notice-btn" onClick={onFlag} disabled={readOnly} data-testid="fp-notice-flag">
                            <Flag size={16} />
                            {t("fp_notice_flag")}
                        </button>
                    ) : (
                        <button type="button" className={`btn ${allCollected ? "btn-primary" : "btn-secondary"} fp-notice-btn`} onClick={onContinue} data-testid="fp-notice-continue">
                            {t("fp_notice_continue")}
                            <ArrowRight size={16} />
                        </button>
                    )}
                    {onLeave && (
                        <button type="button" className="btn btn-ghost fp-notice-btn" onClick={onLeave} data-testid="fp-notice-leave">
                            <ArrowLeft size={16} />
                            {t("fp_notice_leave")}
                        </button>
                    )}
                </div>
            </div>
            {!pending && (
                <HiddenScanInput
                    value={scanValue}
                    onChange={setScanValue}
                    onSubmit={doSubmit}
                    disabled={!!readOnly}
                />
            )}
        </div>
    );
};

export default FlaggedPickNotice;
